import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, ChevronLeft, ChevronRight, Search, Filter, AlertTriangle, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import * as XLSX from 'xlsx';

interface ChargemasterFileViewerProps {
  fileUrl: string;
  fileName: string;
  fileType: string;
  fileId?: string;
  headerRowNumber?: number;
  onClose?: () => void;
  onHeaderRowChange?: (rowNumber: number) => void;
  /** When set, a fetch/parse failure is reported here (the archive tab falls
   *  back to its imported preview) instead of rendering the red error card. */
  onLoadFailed?: (reason?: string) => void;
}

interface DataRow {
  [key: string]: any;
}

/** Blank or duplicate header cells (the CMS wide CSV pads its first row with
 *  dozens of empty columns) become "Column N" / "name (2)" — a Radix Select
 *  item with an empty value throws and took the whole route down. */
function normalizeHeaders(raw: unknown[]): string[] {
  const seen = new Map<string, number>();
  return raw.map((h, i) => {
    let name = h == null ? '' : String(h).trim();
    if (!name) name = `Column ${i + 1}`;
    const n = (seen.get(name) || 0) + 1;
    seen.set(name, n);
    return n > 1 ? `${name} (${n})` : name;
  });
}

export function ChargemasterFileViewer({
  fileUrl,
  fileName,
  fileType,
  fileId,
  headerRowNumber = 1,
  onClose,
  onHeaderRowChange,
  onLoadFailed,
}: ChargemasterFileViewerProps) {
  const [data, setData] = useState<DataRow[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterColumn, setFilterColumn] = useState('all');
  const [filterValue, setFilterValue] = useState('');
  const [currentHeaderRow, setCurrentHeaderRow] = useState(headerRowNumber);
  const [fileSizeBytes, setFileSizeBytes] = useState<number | null>(null);
  const [showLargeFileWarning, setShowLargeFileWarning] = useState(false);
  const [fileSizeCheckComplete, setFileSizeCheckComplete] = useState(false);

  // Large file thresholds
  const LARGE_FILE_WARNING_SIZE = 50 * 1024 * 1024; // 50 MB
  const VERY_LARGE_FILE_SIZE = 100 * 1024 * 1024; // 100 MB

  useEffect(() => {
    checkFileSize();
  }, [fileUrl]);

  useEffect(() => {
    // Only auto-load after file size check completes AND if file is small enough
    if (fileSizeCheckComplete && !showLargeFileWarning) {
      loadFile();
    }
  }, [fileUrl, fileType, currentHeaderRow, fileSizeCheckComplete, showLargeFileWarning]);

  const checkFileSize = async () => {
    setFileSizeCheckComplete(false);
    try {
      const response = await fetch(fileUrl, { method: 'HEAD' });
      if (!response.ok) throw new Error(`HEAD ${response.status}`);
      const size = parseInt(response.headers.get('content-length') || '0');
      setFileSizeBytes(size);

      if (size >= LARGE_FILE_WARNING_SIZE) {
        setShowLargeFileWarning(true);
      } else {
        setShowLargeFileWarning(false);
      }
    } catch (err) {
      console.error('Error checking file size:', err);
      setFileSizeBytes(null);
      setShowLargeFileWarning(false);
      if (onLoadFailed) { onLoadFailed(); return; }
    } finally {
      setFileSizeCheckComplete(true);
    }
  };

  const loadFile = async () => {
    setLoading(true);
    setError(null);

    try {
      if (fileType === 'xlsx' || fileType === 'xls') {
        // Load Excel file with SheetJS
        const response = await fetch(fileUrl);
        if (!response.ok) throw new Error(`GET ${response.status}`);
        const arrayBuffer = await response.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });

        // Get first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length === 0) {
          setError('Empty Excel file');
          return;
        }

        // Use specified row as headers (1-based, so subtract 1 for array index)
        const headerRowIndex = currentHeaderRow - 1;

        if (headerRowIndex >= jsonData.length) {
          setError(`Header row ${currentHeaderRow} is beyond the file length`);
          return;
        }

        const headers = normalizeHeaders((jsonData[headerRowIndex] as unknown[]) || []);
        setColumns(headers);

        // Data rows start after header row
        const rows = jsonData.slice(headerRowIndex + 1).map((row: any) => {
          const rowData: DataRow = {};
          headers.forEach((header, index) => {
            rowData[header] = row[index] !== undefined ? row[index] : '';
          });
          return rowData;
        });

        setData(rows);
      } else {
        const response = await fetch(fileUrl);
        if (!response.ok) throw new Error(`GET ${response.status}`);
        const text = await response.text();

        if (fileType === 'csv') {
          parseCSV(text);
        } else if (fileType === 'json') {
          parseJSON(text);
        } else {
          setError('Unsupported file type for inline viewing');
        }
      }
    } catch (err) {
      console.error('Error loading file:', err);
      if (onLoadFailed) { onLoadFailed(); return; }
      setError('Failed to load file');
    } finally {
      setLoading(false);
    }
  };

  // Parse-level failures (empty file, bad header row, unsupported type) also
  // hand control back to the parent when it asked for that.
  useEffect(() => {
    if (error && onLoadFailed) onLoadFailed(error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const parseCSV = (text: string) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length === 0) {
      setError('Empty file');
      return;
    }

    // Simple CSV parser (for production, use a library like PapaParse)
    const parseRow = (row: string) => {
      const result = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < row.length; i++) {
        const char = row[i];

        if (char === '"') {
          if (inQuotes && row[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          result.push(current);
          current = '';
        } else {
          current += char;
        }
      }

      result.push(current);
      return result.map(field => field.trim());
    };

    // Use specified row as headers (1-based, so subtract 1 for array index)
    const headerRowIndex = currentHeaderRow - 1;

    if (headerRowIndex >= lines.length) {
      setError(`Header row ${currentHeaderRow} is beyond the file length`);
      return;
    }

    const headers = normalizeHeaders(parseRow(lines[headerRowIndex]));
    setColumns(headers);

    // Data rows start after header row
    const rows = lines.slice(headerRowIndex + 1).map(line => {
      const values = parseRow(line);
      const row: DataRow = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      return row;
    });

    setData(rows);
  };

  const parseJSON = (text: string) => {
    try {
      const jsonData = JSON.parse(text);

      if (Array.isArray(jsonData) && jsonData.length > 0) {
        const firstItem = jsonData[0];
        setColumns(Object.keys(firstItem));
        setData(jsonData);
      } else {
        setError('Invalid JSON format or empty array');
      }
    } catch (err) {
      setError('Failed to parse JSON file');
    }
  };

  // Apply filters and search
  const filteredData = data.filter(row => {
    // Apply column filter
    if (filterColumn && filterColumn !== 'all' && filterValue) {
      const cellValue = String(row[filterColumn] || '').toLowerCase();
      if (!cellValue.includes(filterValue.toLowerCase())) {
        return false;
      }
    }

    // Apply search across all columns
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return columns.some(col =>
        String(row[col] || '').toLowerCase().includes(searchLower)
      );
    }

    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  const downloadFile = () => {
    window.open(fileUrl, '_blank');
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleProceedWithLargeFile = () => {
    setShowLargeFileWarning(false);
    loadFile();
  };

  // Show loading while checking file size
  if (!fileSizeCheckComplete) {
    return (
      <Card className="w-full h-full">
        <CardContent className="flex flex-col items-center justify-center h-96 space-y-4">
          <Loader2 className="w-12 h-12 text-primary animate-spin" />
          <div className="text-center space-y-2">
            <div className="text-lg font-medium">Checking file size...</div>
            <div className="text-xs text-muted-foreground">
              Determining if this file can be previewed in the browser
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (showLargeFileWarning && fileSizeBytes && fileSizeBytes >= LARGE_FILE_WARNING_SIZE) {
    const isVeryLarge = fileSizeBytes >= VERY_LARGE_FILE_SIZE;

    return (
      <Card className="w-full h-full">
        <CardContent className="flex flex-col items-center justify-center h-96 space-y-4 p-8">
          <AlertTriangle className={`w-16 h-16 ${isVeryLarge ? 'text-red-500' : 'text-yellow-500'}`} />
          <div className="text-center space-y-2">
            <h3 className="text-lg font-semibold">
              {isVeryLarge ? 'Very Large File Detected' : 'Large File Warning'}
            </h3>
            <p className="text-muted-foreground max-w-md">
              This file is <strong>{formatBytes(fileSizeBytes)}</strong> in size.
              {isVeryLarge
                ? ' Loading very large files in the browser may cause performance issues or crashes. Consider downloading the file instead.'
                : ' Loading this file may take a moment and could slow down your browser.'}
            </p>
          </div>
          <Alert className={`max-w-md ${isVeryLarge ? 'border-red-500' : 'border-yellow-500'}`}>
            <AlertTriangle className={`h-4 w-4 ${isVeryLarge ? 'text-red-500' : 'text-yellow-500'}`} />
            <AlertDescription>
              {isVeryLarge
                ? 'We recommend downloading this file instead of viewing it in the browser.'
                : 'Large files may take 10-30 seconds to load. Please be patient.'}
            </AlertDescription>
          </Alert>
          <div className="flex items-center space-x-3">
            <Button onClick={downloadFile} variant="default">
              <Download className="w-4 h-4 mr-2" />
              Download File Instead
            </Button>
            <Button onClick={handleProceedWithLargeFile} variant="outline">
              Load Anyway
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="w-full h-full">
        <CardContent className="flex flex-col items-center justify-center h-96 space-y-4">
          <Loader2 className="w-12 h-12 text-primary animate-spin" />
          <div className="text-center space-y-2">
            <div className="text-lg font-medium">Loading file...</div>
            {fileSizeBytes && (
              <div className="text-sm text-muted-foreground">
                Processing {formatBytes(fileSizeBytes)} {fileType.toUpperCase()} file
              </div>
            )}
            <div className="text-xs text-muted-foreground">
              This may take a moment for large files
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full h-full">
        <CardContent className="flex flex-col items-center justify-center h-96 space-y-4">
          <div className="text-red-500">{error}</div>
          <Button onClick={downloadFile}>
            <Download className="w-4 h-4 mr-2" />
            Download File
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full h-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{fileName}</CardTitle>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={downloadFile}>
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
            {onClose && (
              <Button variant="ghost" size="sm" onClick={onClose}>
                Close
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Controls */}
        <div className="mb-4 space-y-4">
          {/* Header Row Selector */}
          <div className="flex items-center space-x-2 pb-2 border-b">
            <span className="text-sm font-medium">Header Row:</span>
            <Input
              type="number"
              min="1"
              max="100"
              value={currentHeaderRow}
              onChange={(e) => {
                const newRow = parseInt(e.target.value) || 1;
                setCurrentHeaderRow(newRow);
                if (onHeaderRowChange) {
                  onHeaderRowChange(newRow);
                }
              }}
              className="w-20"
            />
            <span className="text-xs text-muted-foreground">
              (Row number containing column headers)
            </span>
          </div>

          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="flex-1 max-w-sm">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search all columns..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Column Filter */}
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <Select
                value={filterColumn}
                onValueChange={(value) => {
                  setFilterColumn(value);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Filter column" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All columns</SelectItem>
                  {columns.map(col => (
                    <SelectItem key={col} value={col}>
                      {col}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {filterColumn && filterColumn !== 'all' && (
                <Input
                  type="text"
                  placeholder="Filter value..."
                  value={filterValue}
                  onChange={(e) => {
                    setFilterValue(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-40"
                />
              )}
            </div>

            {/* Rows per page */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Rows:</span>
              <Select
                value={String(rowsPerPage)}
                onValueChange={(value) => {
                  setRowsPerPage(Number(value));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="250">250</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center justify-between text-sm text-gray-600">
            <div>
              Showing {startIndex + 1} to {Math.min(endIndex, filteredData.length)} of {filteredData.length} rows
              {filteredData.length !== data.length && ` (filtered from ${data.length} total)`}
            </div>
            <div>
              {columns.length} columns
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    #
                  </th>
                  {columns.map(col => (
                    <th
                      key={col}
                      className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentData.map((row, rowIndex) => (
                  <tr key={rowIndex} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {startIndex + rowIndex + 1}
                    </td>
                    {columns.map(col => (
                      <td
                        key={col}
                        className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap max-w-xs truncate"
                        title={String(row[col] || '')}
                      >
                        {row[col] || '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}